/**
 * Whether a session can be forked, before a turn or from its latest message.
 * The server enforces this under the parent's control lease, and the client
 * shows `reason` as the disabled tooltip of every fork action, so this module
 * stays pure like `rewind-policy.ts`, whose checks it shares.
 */
import type { RewindPolicySession, RewindPolicyTurn } from './rewind-policy';

import { boundaryDecision, REWIND_BLOCKED_REASON } from './rewind-policy';

export type ForkPolicySession = RewindPolicySession & {
  /** A waiting rewind hides turns the fork would otherwise copy. */
  rewindTurnId?: string | null;
};

/**
 * What a session list knows of a session it has not opened: its status, with
 * work the list sees in flight as `running`, and a waiting rewind. A list reads
 * no turns, so only the server refuses a conversation with nothing to copy.
 */
export type ListedForkSession = Pick<ForkPolicySession, 'status' | 'rewindTurnId'>;

export type ForkDecision = { ok: true } | { ok: false; reason: string };

export const FORK_BLOCKED_REASON = {
  ...REWIND_BLOCKED_REASON,
  RUNNING: 'Stop the current turn to fork',
  REWINDING: 'Send or cancel the edit to fork',
  EMPTY: 'No messages to fork from',
  NO_SANDBOX: "This chat's sandbox could not start, so it can't be forked",
  MOVE_PENDING: "Send a message to finish starting this chat's sandbox, then fork",
} as const;

function forkDecision(session: ForkPolicySession, turn?: RewindPolicyTurn): ForkDecision {
  const decision = boundaryDecision(session, turn);
  if (!decision.ok) {
    return decision;
  }
  // Forking copies a settled conversation: the parent's VM is stopped to snapshot it.
  if (decision.stopsTurn) {
    return { ok: false, reason: FORK_BLOCKED_REASON.RUNNING };
  }
  if (session.rewindTurnId) {
    return { ok: false, reason: FORK_BLOCKED_REASON.REWINDING };
  }
  return { ok: true };
}

/** The reasons that apply whatever message the fork starts from. */
export function canForkSession(session: ForkPolicySession): ForkDecision {
  return forkDecision(session);
}

/**
 * The reasons a session list can tell, whether the fork starts from the latest
 * message or from one picked in the Fork from message dialog. The server
 * checks the turns as well.
 */
export function canForkFromList(session: ListedForkSession): ForkDecision {
  // Without turns, only the session's own state decides. Lists show main sessions only.
  return forkDecision({ ...session, turns: [], sideAgentParentId: null });
}

/** A turn that reached OpenCode and was not discarded; a fork copies these. */
export function isForkableTurn(turn: Pick<RewindPolicyTurn, 'delivered' | 'discardedAt'>) {
  return !turn.discardedAt && turn.delivered;
}

/**
 * Fork before `turn`, or from the latest message when it is omitted. The
 * session is active and idle (a stopped sandbox is fine: it is forked from its
 * snapshot), no question or rewind waits, and the turn reached OpenCode and
 * was not discarded.
 */
export function canForkFromTurn(session: ForkPolicySession, turn?: RewindPolicyTurn): ForkDecision {
  const decision = forkDecision(session, turn);
  if (decision.ok && turn === undefined && !session.turns.some(isForkableTurn)) {
    return { ok: false, reason: FORK_BLOCKED_REASON.EMPTY };
  }
  return decision;
}
